import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { trackPromise } from 'react-promise-tracker';
import toastService from './ToastService';
import { HTTPError, HTTPSuccess } from '../static/Messages';

export interface OnErrorProps {
  message?: string;
  doReturn?: () => unknown;
  doOnError?: (e: AxiosError) => void;
}

export interface OnSuccessProps<O> {
  message?: string;
  doOnSuccess?: (response: O) => void;
}

function handleError(e: AxiosError, onError?: OnErrorProps) {
  onError?.doOnError
    ? // toast should be called externally then
      onError.doOnError(e)
    : toastService.popMessage({
        type: toastService.toastType.error,
        message: onError?.message || HTTPError.default,
        head: HTTPError.head,
      });

  return onError?.doReturn && onError.doReturn();
}

const callGetApi = <T>(
  path: string,
  config: AxiosRequestConfig,
  onSuccess?: OnSuccessProps<T>,
  onError?: OnErrorProps,
): Promise<T> => {
  return trackPromise(axios.get(path, config))
    .then((response) => {
      onSuccess?.doOnSuccess && onSuccess.doOnSuccess(response.data);
      return response.data;
    })

    .catch((e: AxiosError) => {
      return handleError(e, onError);
    });
};

const callPutApi = <I, O>(
  path: string,
  payload: I,
  config: AxiosRequestConfig,
  onSuccess?: OnSuccessProps<O>,
  onError?: OnErrorProps,
): Promise<O> => {
  return axios
    .put(path, payload, config)
    .then((response) => {
      toastService.popMessage({
        type: toastService.toastType.confirm,
        message: onSuccess?.message || HTTPSuccess.updated,
        head: HTTPSuccess.head,
      });
      onSuccess?.doOnSuccess && onSuccess.doOnSuccess(response.data);
      return response.data;
    })
    .catch((e) => {
      return handleError(e, onError);
    });
};

const callPostApi = <I, O>(
  path: string,
  payload: I,
  config: AxiosRequestConfig,
  onSuccess?: OnSuccessProps<O>,
  onError?: OnErrorProps,
): Promise<O> => {
  return axios
    .post(path, payload, config)
    .then((response) => {
      toastService.popMessage({
        type: toastService.toastType.confirm,
        message: onSuccess?.message || HTTPSuccess.saved,
        head: HTTPSuccess.head,
      });
      onSuccess?.doOnSuccess && onSuccess.doOnSuccess(response.data);
      return response.data;
    })
    .catch((e) => {
      return handleError(e, onError);
    });
};

const apiService = {
  callGet: callGetApi,
  callPut: callPutApi,
  callPost: callPostApi,
};

export default apiService;
